/**
 * Intercom Service
 * 
 * Manages Intercom messenger widget with JWT-based Identity Verification.
 * All user and company attributes are signed in the JWT for verified updates.
 */

import Intercom from '@intercom/messenger-js-sdk';
import { authService } from '@/services/auth';
import { scmPermissionsService } from '@/services/scm-permissions';
import { hasUnsubmittedChanges } from '@/utils/changeDetection';

const APP_ID = 'q1ztfxow';

class IntercomService {
  constructor() {
    this.launcherHidden = false;
    this.isInitialized = false;
    this.lastJwtHash = null;
  }

  /**
   * Get API URL based on environment
   */
  getApiUrl() {
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
      return 'http://127.0.0.1:1337';
    }
    return process.env.GATSBY_STRAPI_URL;
  }

  /**
   * Get auth token from Keycloak
   */
  getToken() {
    return authService.getAuthState()?.token;
  }

  /**
   * Get industry name from ID
   * BusinessSegmentRegistration can be:
   * - A numeric database ID (e.g., 1, 2, 3)
   * - An IndustryId code string (e.g., "IND001")
   */
  getIndustryName(industryId, appStore) {
    if (!industryId) return null;

    const industries = appStore?.state?.data?.regIndustries || [];

    // Convert to string for comparison since it could be number or string
    const searchId = String(industryId);

    const industry = industries.find(ind =>
      String(ind.id) === searchId ||
      String(ind.code) === searchId ||
      ind.name === searchId  // Also check if it's already the name
    );

    // Industry lookup failure silenced - not actionable

    return industry?.name || null;
  }

  /**
   * Get company size as employee count for Intercom
   * Intercom's "size" is a reserved attribute expecting a number (employee count)
   * CompanySize is stored as numeric ID (0-5), we map to representative employee counts
   */
  getCompanySizeNumber(sizeId) {
    // Ensure we're working with a number
    const numericId = typeof sizeId === 'string' ? parseInt(sizeId, 10) : sizeId;

    // Map to representative employee counts for each range
    const sizeNumbers = {
      0: null,    // Not specified - don't send
      1: 25,      // less than 50 → 25
      2: 150,     // 50-249 → 150
      3: 500,     // 250-999 → 500
      4: 2500,    // 1000-4999 → 2500
      5: 5000     // 5000 and more → 5000
    };

    return sizeNumbers[numericId] ?? null;
  }

  /**
   * Build user data object from appStore
   * Gets user name from the logged-in user (users store or Keycloak profile),
   * NOT from parsedProfile which always contains the main user's Snowflake data.
   */
  buildUserData(appStore) {
    const email = authService.getAuthState()?.user?.email;
    if (!email) return null;

    // Prefer users store — reflects personal settings changes immediately
    // User objects have combined `name` field (e.g. "Alex Hitchcock"), not separate firstName/lastName
    const users = appStore?.state?.data?.users?.list || [];
    const currentUser = users.find(u => u.email === email);
    if (currentUser?.name) {
      return currentUser.name;
    }

    // Fallback to Keycloak profile
    const user = authService.getAuthState()?.user;
    return user?.name || null;
  }

  /**
   * Build company data object from appStore
   */
  buildCompanyData(appStore) {
    if (!appStore?.state?.data) return null;

    const projectId = authService.getProjectId();
    if (!projectId) return null;

    const { parsedProfile, companyQuestions, customerRequests } = appStore.state.data;
    const realm = process.env.GATSBY_KEYCLOAK_REALM || 'platform';

    // Build company object
    const company = {
      project_id: projectId,
      company_id: `${realm}_${projectId}`,
      realm: realm,
    };

    // Company name
    if (parsedProfile?.company?.Name) {
      company.name = parsedProfile.company.Name;
    }

    // Company size - Intercom expects a number (employee count)
    const rawCompanySize = parsedProfile?.company?.CompanySize;
    if (rawCompanySize !== undefined && rawCompanySize !== null) {
      const sizeNumber = this.getCompanySizeNumber(rawCompanySize);
      if (sizeNumber !== null) {
        company.size = sizeNumber;
      }
    }

    // Company industry - get name from BusinessSegmentRegistration ID
    if (parsedProfile?.company?.BusinessSegmentRegistration) {
      const industryName = this.getIndustryName(
        parsedProfile.company.BusinessSegmentRegistration,
        appStore
      );
      if (industryName) {
        company.industry = industryName;
      }
    }

    // Permission plan
    try {
      const permissions = scmPermissionsService.permissions;
      if (!permissions?.canAccess) {
        company.plan = 'RESTRICTED';
      } else if (permissions.isTrialMode) {
        company.plan = 'GRANTED_TRIAL';
      } else {
        company.plan = 'GRANTED_PAID';
      }
    } catch {
      company.plan = 'RESTRICTED';
    }

    // Self-assessment data from companyQuestions
    const questions = companyQuestions?.data;
    if (questions?.questionnaire && Array.isArray(questions.questionnaire)) {
      // Carbon footprint toggle (ccf1) - THIS IS THE CORRECT CHECK
      const carbonFootprintToggle = questions.questionnaire.find(q => 
        q.TranslationKey?.endsWith('ccf1')
      );
      company.carbon_footprint = carbonFootprintToggle?.Answer?.toLowerCase() === 'yes';

      // Scope 1+2 reduction target (ctd1)
      const s12Toggle = questions.questionnaire.find(q => q.TranslationKey?.endsWith('ctd1'));
      company.reduction_target_s12 = s12Toggle?.Answer?.toLowerCase() === 'yes';

      // Scope 3 upstream reduction target (cti1)
      const s3uToggle = questions.questionnaire.find(q => q.TranslationKey?.endsWith('cti1'));
      company.reduction_target_s3u = s3uToggle?.Answer?.toLowerCase() === 'yes';
    } else {
      company.reduction_target_s12 = false;
      company.reduction_target_s3u = false;
      company.carbon_footprint = false;
    }

    // Last submit date from customerRequests
    if (customerRequests?.lastSubmitDate) {
      company.last_submit_date = customerRequests.lastSubmitDate;
    }

    // Unsubmitted changes
    company.unsubmitted_changes = hasUnsubmittedChanges();

    return company;
  }

  /**
   * Generate hash of all data for change detection
   */
  hashData(company, userName) {
    return JSON.stringify({ company, userName });
  }

  /**
   * Fetch JWT from backend with user and company data
   */
  async fetchJWT(companyData = null, userName = null) {
    const token = this.getToken();
    if (!token) return null;

    try {
      const response = await fetch(`${this.getApiUrl()}/api/intercom/generate-jwt`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ company: companyData, userName: userName })
      });

      if (!response.ok) {
        console.error('[Intercom] JWT fetch failed:', response.status);
        return null;
      }

      const data = await response.json();
      return data.token;
    } catch (error) {
      console.error('[Intercom] JWT fetch error:', error.message);
      return null;
    }
  }

  /**
   * Initialize Intercom with user and company data
   */
  async initialize(appStore = null) {
    if (process.env.GATSBY_INTERCOM_ENABLED !== 'true') {
      console.log('[Intercom] Disabled by environment');
      return false;
    }

    const authState = authService.getAuthState();
    if (!authState?.authenticated || !authState.user?.email) {
      console.log('[Intercom] User not authenticated');
      return false;
    }

    try {
      const store = appStore || (typeof window !== 'undefined' ? window.appStore : null);
      const companyData = this.buildCompanyData(store);
      const userName = this.buildUserData(store);
      
      // Fetch JWT with all data signed
      const jwt = await this.fetchJWT(companyData, userName);
      if (!jwt) {
        console.error('[Intercom] Failed to get JWT');
        return false;
      }

      // Store hash for change detection
      this.lastJwtHash = this.hashData(companyData, userName);

      // Boot Intercom with JWT only - all data is in the JWT.
      // z_index sits between page chrome (SCM filter bar z-40, table header z-20)
      // and HeadlessUI Dialog/Drawer overlays (z-50). Notifications (z-2000) and
      // dropdowns (z-100/200) intentionally remain above.
      const config = {
        app_id: APP_ID,
        intercom_user_jwt: jwt,
        alignment: 'right',
        horizontal_padding: 32,
        vertical_padding: 32,
        z_index: 45,
        // A page may have asked to hide the launcher before Intercom booted
        // (the coach panel opens first on a cold load): honour it at boot.
        hide_default_launcher: this.launcherHidden,
      };

      Intercom(config);
      this.isInitialized = true;

      // Intercom init logging silenced

      return true;
    } catch (error) {
      console.error('[Intercom] Initialization error:', error);
      return false;
    }
  }

  /**
   * Update Intercom with fresh JWT containing latest data
   * Called when appStore data changes
   */
  async update(appStore) {
    if (!this.isInitialized) {
      console.log('[Intercom] Not initialized, skipping update');
      return false;
    }

    try {
      const companyData = this.buildCompanyData(appStore);
      const userName = this.buildUserData(appStore);
      const newHash = this.hashData(companyData, userName);

      // Skip if nothing changed
      if (newHash === this.lastJwtHash) {
        return true;
      }

      // Fetch fresh JWT with new data
      const jwt = await this.fetchJWT(companyData, userName);
      if (!jwt) {
        console.error('[Intercom] Failed to get JWT for update');
        return false;
      }

      // Update hash
      this.lastJwtHash = newHash;

      // Update Intercom with new JWT
      if (typeof window !== 'undefined' && window.Intercom) {
        window.Intercom('update', {
          intercom_user_jwt: jwt
        });

        // Intercom update logging silenced
      }

      return true;
    } catch (error) {
      console.error('[Intercom] Update error:', error);
      return false;
    }
  }

  /**
   * Hide or show the default launcher. Remembered across the boot, so a page
   * that hides it before Intercom is initialised still gets a hidden launcher
   * once it boots — on demo the coach panel was open before boot and the
   * launcher sat on the chat's send button (Alex, 08.09).
   */
  setLauncherHidden(hidden) {
    this.launcherHidden = !!hidden;
    if (this.isInitialized && typeof window !== 'undefined' && typeof window.Intercom === 'function') {
      window.Intercom('update', { hide_default_launcher: this.launcherHidden });
    }
  }

  /**
   * Check if service is ready
   */
  isReady() {
    return this.isInitialized;
  }

  /**
   * Shutdown Intercom and clear session
   */
  shutdown() {
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('shutdown');
      this.isInitialized = false;
      this.lastJwtHash = null;

      // Shutdown logging silenced
    }
  }
}

export const intercomService = new IntercomService();
export default intercomService;
